import { useSiteSettings } from '@/lib/siteContent';
/**
 * Data hooks for the storefront. Requests are de-duplicated at module scope so
 * the home page fetches `/services` once for the hero, the venue rail and the
 * grid instead of three times.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getAllServiceCategories,
  getAllServices,
  getServiceById,
  getServiceProviders,
  getShowcaseEvents,
  getSubcategories,
  getServedCities,
  getArtists,
  getArtistBySlug,
} from '@/api/apiUtils';
import { useEvents } from '@/context/EventContext';
import {
  isPubliclyVisible,
  normalizeProvider,
  normalizeService,
  readStoredLocation,
} from '@/lib/catalog';
import { isUpcoming } from '@/lib/filters';

/** The Events vertical. Everything on this site hangs off it. */
/** Fallback only — the live value is the admin setting `events_category_id`. */
export const EVENTS_CATEGORY_ID = 7;

/** The category the site treats as "events" (LK Admin → Settings → Brand & contact → Discovery). */
export const useEventsCategoryId = () => Number(useSiteSettings().events_category_id) || EVENTS_CATEGORY_ID;

const inflight = new Map();

const once = (key, loader) => {
  if (!inflight.has(key)) {
    inflight.set(
      key,
      loader().catch((err) => {
        // Don't cache failures — the next mount should get a fresh attempt.
        inflight.delete(key);
        throw err;
      })
    );
  }
  return inflight.get(key);
};

export const invalidateCatalog = () => inflight.clear();

/** Generic async state wrapper so every hook reports the same shape. */
const useAsync = (loader, deps = []) => {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    loader()
      .then((data) => !cancelled && setState({ data, loading: false, error: null }))
      .catch((error) => !cancelled && setState({ data: null, loading: false, error }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => {
    invalidateCatalog();
    setNonce((n) => n + 1);
  }, []);

  return { ...state, reload };
};

/**
 * Where "x km away" is measured from: the visitor's chosen city. Keyed on the
 * city so switching cities re-measures; the old version was memoised once per
 * mount and kept measuring from wherever the page first loaded.
 */
const useCityOrigin = () => {
  const { selectedCity } = useEvents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(readStoredLocation, [selectedCity?.lat, selectedCity?.lng, selectedCity?.zoneId]);
};

/* ------------------------------------------------------------------ queries */

export const useCategories = () => {
  const { data, loading, error, reload } = useAsync(
    () =>
      once('categories', async () => {
        const res = await getAllServiceCategories('active', true);
        const list = Array.isArray(res?.data) ? res.data : [];
        return list
          .filter((c) => c?.status === 'active')
          .sort((a, b) => (a.displayOrder ?? 99) - (b.displayOrder ?? 99));
      }),
    []
  );
  return { categories: data || [], loading, error, reload };
};

/**
 * The browse tiles. Ordering, artwork and copy all come from the admin panel.
 *
 * A tile with nothing on is hidden unless it is featured — that is how a
 * seasonal bucket (Christmas) goes up before anything is tagged with it, while
 * the row still never renders a tile that leads to an empty page.
 */
export const useSubcategories = (categoryIdArg) => {
  const eventsCategoryId = useEventsCategoryId();
  const categoryId = categoryIdArg ?? eventsCategoryId;
  const { data, loading, error, reload } = useAsync(
    () =>
      once(`subcategories:${categoryId}`, async () => {
        const res = await getSubcategories(categoryId);
        return Array.isArray(res?.data) ? res.data : [];
      }),
    [categoryId]
  );

  const all = data || [];
  return {
    subcategories: all,
    browsable: all.filter((s) => s.eventCount > 0 || s.isFeatured),
    loading,
    error,
    reload,
  };
};

/** Events already held — the "we've hosted" wall. */
export const usePastEvents = (categoryIdArg, limit = 24) => {
  const eventsCategoryId = useEventsCategoryId();
  const categoryId = categoryIdArg ?? eventsCategoryId;
  const { data, loading, error } = useAsync(
    () =>
      once(`showcase:${categoryId}:${limit}`, async () => {
        const res = await getShowcaseEvents(categoryId, limit);
        return Array.isArray(res?.data) ? res.data : [];
      }),
    [categoryId, limit]
  );
  return { pastEvents: data || [], loading, error };
};

export const useEventList = (categoryIdArg) => {
  const eventsCategoryId = useEventsCategoryId();
  const categoryId = categoryIdArg ?? eventsCategoryId;
  const origin = useCityOrigin();
  const { data, loading, error, reload } = useAsync(
    () =>
      once(`services:${categoryId}`, async () => {
        const res = await getAllServices(categoryId, 'active', 'approved');
        const list = Array.isArray(res?.data) ? res.data : [];
        return list;
      }).then((list) =>
        list.map((s) => normalizeService(s, origin)).filter(isPubliclyVisible)
      ),
    [categoryId, origin.lat, origin.lng]
  );
  return { events: data || [], loading, error, reload };
};

/**
 * One event. `/providers/services/:id` is authoritative for variants and
 * attributes but omits the `provider` and `category` joins, and
 * `/providers/:id` is auth-only — so the venue and category are hydrated from
 * the public list endpoint, which does include them.
 */
export const useEvent = (id, categoryIdArg) => {
  const eventsCategoryId = useEventsCategoryId();
  const categoryId = categoryIdArg ?? eventsCategoryId;
  const origin = useCityOrigin();
  const { data, loading, error, reload } = useAsync(async () => {
    if (!id) return null;

    const [detail, list] = await Promise.all([
      once(`service:${id}`, () => getServiceById(id)),
      once(`services:${categoryId}`, async () => {
        const res = await getAllServices(categoryId, 'active', 'approved');
        return Array.isArray(res?.data) ? res.data : [];
      }).catch(() => []),
    ]);

    const raw = detail?.data;
    if (!raw) return null;

    const listed = list.find((s) => String(s.id) === String(id));
    return normalizeService(
      {
        ...raw,
        provider: raw.provider ?? listed?.provider ?? null,
        category: raw.category ?? listed?.category ?? null,
      },
      origin
    );
  }, [id, categoryId, origin.lat, origin.lng]);

  return { event: data, loading, error, reload };
};

/** Venues = approved providers in the Events category, nearest first. */
export const useVenues = (categoryId = EVENTS_CATEGORY_ID) => {
  const origin = useCityOrigin();
  const { data, loading, error, reload } = useAsync(
    () =>
      once(`providers:${categoryId}:${origin.lat},${origin.lng}`, () =>
        getServiceProviders({
          categoryId,
          lat: origin.lat,
          lng: origin.lng,
          status: 'active',
          approval_status: 'approved',
        })
      ).then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        return list
          .map((p) => normalizeProvider(p, origin))
          .filter((p) => p?.isActive)
          .sort((a, b) => (a.distanceKm ?? 1e9) - (b.distanceKm ?? 1e9));
      }),
    [categoryId, origin.lat, origin.lng]
  );
  return { venues: data || [], loading, error, reload };
};

/** Venues joined with the events happening at them ("NEXT UP" rail). */
export const useVenuesWithEvents = (categoryId = EVENTS_CATEGORY_ID) => {
  const { venues: allVenues, loading: venuesLoading, error: venuesError } = useVenues(categoryId);
  const { events, loading: eventsLoading } = useEventList(categoryId);
  const { visibleZoneIds, servedZoneIds } = useCity();
  const venues = useMemo(
    () => allVenues.filter((v) => inCity(v.zoneId, visibleZoneIds, servedZoneIds)),
    [allVenues, visibleZoneIds, servedZoneIds]
  );

  const data = useMemo(() => {
    if (!venues.length) return [];
    const byProvider = new Map();
    // A venue rail should only advertise events you can still attend.
    events.filter(isUpcoming).forEach((e) => {
      const list = byProvider.get(e.providerId) || [];
      list.push(e);
      byProvider.set(e.providerId, list);
    });
    return venues
      .map((venue) => {
        const list = (byProvider.get(venue.id) || []).sort((a, b) =>
          String(a.startDate || '').localeCompare(String(b.startDate || ''))
        );
        return { ...venue, events: list, nextEvent: list[0] || null };
      })
      // A venue with nothing on is not worth a card.
      .filter((v) => v.events.length > 0);
  }, [venues, events]);

  return { venues: data, loading: venuesLoading || eventsLoading, error: venuesError };
};

/* ------------------------------------------------------------------ cities */

/**
 * The cities LocalKonnect serves — admin zones (LK Admin → Settings → Zones),
 * busiest first. Each carries `alsoShowZoneIds`: other zones whose events it
 * also shows, so a new city (Jodhpur) isn't an empty page on day one.
 */
export const useServedCities = () => {
  const { data, loading, error } = useAsync(
    () =>
      once('cities', async () => {
        const res = await getServedCities();
        const zones = res?.data?.zones ?? [];
        return zones.map((z) => ({
          zoneId: Number(z.zoneId),
          name: z.displayName || z.city || z.name,
          state: z.state || '',
          lat: z.lat != null ? Number(z.lat) : null,
          lng: z.lng != null ? Number(z.lng) : null,
          providerCount: Number(z.providerCount) || 0,
          alsoShowZoneIds: (z.alsoShowZoneIds ?? []).map(Number),
        }));
      }),
    []
  );
  return { cities: data || [], loading, error };
};

/**
 * Should something in `zoneId` show for the visitor's city?
 *
 * Only hides what belongs to a *known, different* served city. Unmapped
 * organisers (null zone) and zones missing from the public list (inactive,
 * or cities failed to load) always show — a gap in zone setup must never make
 * existing events disappear.
 */
export const inCity = (zoneId, visibleZoneIds, servedZoneIds) => {
  if (!visibleZoneIds || zoneId == null) return true;
  const id = Number(zoneId);
  if (servedZoneIds && !servedZoneIds.has(id)) return true;
  return visibleZoneIds.has(id);
};

/**
 * The visitor's city: the one they picked (by zone id, or by name for choices
 * saved before cities came from admin), else the busiest served city.
 */
export const useCity = () => {
  const { selectedCity } = useEvents();
  const { cities, loading } = useServedCities();
  const city = useMemo(() => {
    if (!cities.length) return null;
    const byId = selectedCity?.zoneId != null && cities.find((c) => c.zoneId === Number(selectedCity.zoneId));
    const byName = cities.find((c) => c.name.toLowerCase() === String(selectedCity?.name || '').toLowerCase());
    return byId || byName || cities[0];
  }, [cities, selectedCity]);
  const visibleZoneIds = useMemo(
    () => (city ? new Set([city.zoneId, ...(city.alsoShowZoneIds || [])]) : null),
    [city]
  );
  const servedZoneIds = useMemo(
    () => (cities.length ? new Set(cities.map((c) => c.zoneId)) : null),
    [cities]
  );
  return { city, cities, visibleZoneIds, servedZoneIds, loading };
};

/**
 * The event list scoped to the visitor's city, plus what the page needs to
 * explain borrowed events: how many upcoming events are the city's own, and
 * which other cities' events are being shown.
 */
export const useCityEvents = (categoryIdArg) => {
  const { events, loading, error, reload } = useEventList(categoryIdArg);
  const { city, cities, visibleZoneIds, servedZoneIds, loading: citiesLoading } = useCity();

  const scoped = useMemo(() => {
    const isOwn = (e) => city != null && e.zoneId != null && Number(e.zoneId) === city.zoneId;
    // The visitor's own city first, then everything else (borrowed cities,
    // unmapped organisers). Both halves keep the API's order, which is LK
    // Admin → Event order, so pinned events still lead within each half.
    const inScope = events.filter((e) => inCity(e.zoneId, visibleZoneIds, servedZoneIds));
    const list = [...inScope.filter(isOwn), ...inScope.filter((e) => !isOwn(e))];
    const upcoming = list.filter(isUpcoming);
    // Unmapped organisers still show everywhere, but don't count as the city's
    // own — otherwise one unzoned event hides the "showing Bikaner" notice.
    const ownUpcomingCount = city ? upcoming.filter(isOwn).length : upcoming.length;
    const borrowedIds = new Set(
      upcoming
        .map((e) => (e.zoneId == null ? null : Number(e.zoneId)))
        .filter((id) => id != null && city && id !== city.zoneId && servedZoneIds?.has(id))
    );
    const borrowedFrom = [...borrowedIds].map((id) => cities.find((c) => c.zoneId === id)?.name).filter(Boolean);
    return { events: list, ownUpcomingCount, borrowedFrom };
  }, [events, city, cities, visibleZoneIds, servedZoneIds]);

  return { ...scoped, city, loading: loading || citiesLoading, error, reload };
};

/* ----------------------------------------------------------------- artists */

/** Artists with live events, featured first (LK Admin → Catalog → Artists). */
export const useArtists = (limit = 50) => {
  const { data, loading, error } = useAsync(
    () =>
      once(`artists:${limit}`, async () => {
        const res = await getArtists(limit);
        return Array.isArray(res?.data) ? res.data : [];
      }),
    [limit]
  );
  return { artists: data || [], loading, error };
};

/** One artist with their upcoming and past events, normalised like every other event list. */
export const useArtist = (slug) => {
  const origin = useCityOrigin();
  const { data, loading, error } = useAsync(async () => {
    if (!slug) return null;
    const res = await once(`artist:${slug}`, () => getArtistBySlug(slug));
    const artist = res?.data;
    if (!artist) return null;
    const norm = (list) => (list || []).map((s) => normalizeService(s, origin)).filter(isPubliclyVisible);
    return { ...artist, upcoming: norm(artist.upcoming), past: norm(artist.past) };
  }, [slug, origin.lat, origin.lng]);
  return { artist: data, loading, error };
};
